import { Injectable, computed, inject, signal } from '@angular/core'

import { toBoolean, toStringOrNull } from './feature-flags.utils'
import { FEATURE_FLAGS } from '../tokens/feature-flags.token'

import type { FeatureKey, FeatureMap, FeatureValues } from './feature-flags.model'

@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private readonly initial = inject(FEATURE_FLAGS)
  private readonly mapSignal = signal<FeatureMap>(this.initial)

  // === Basic checks ===
  has = (key: FeatureKey): boolean => toBoolean(this.mapSignal()[key])
  all = (...keys: FeatureKey[]): boolean => keys.every((k) => this.has(k))
  some = (...keys: FeatureKey[]): boolean => keys.some((k) => this.has(k))
  watch = (key: FeatureKey) => computed(() => this.has(key))

  // === Typed accessors ===
  get = <K extends FeatureKey>(key: K): FeatureValues => this.mapSignal()[key]

  getBoolean = (key: FeatureKey): boolean => toBoolean(this.mapSignal()[key])
  getString = (key: FeatureKey): string | null => toStringOrNull(this.mapSignal()[key])

  watchValue = (key: FeatureKey) => computed<FeatureValues>(() => this.mapSignal()[key])
  watchBoolean = (key: FeatureKey) => computed<boolean>(() => this.getBoolean(key))
  watchString = (key: FeatureKey) => computed<string | null>(() => this.getString(key))

  // === Uptades ===
  update(partial: Partial<FeatureMap>): void {
    this.mapSignal.update((curr) => ({ ...curr, ...partial }))
  }

  replace(next: FeatureMap): void {
    this.mapSignal.set(next)
  }
}
